import { useCallback, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { FOUNDATION_CSS_STYLE_TAG, FOUNDATION_LIQUID_TAG } from './foundation-preview-css';

const MIN_HEIGHT = 250;

type HtmlPreviewProps = {
  body: string;
  /** Applied to the iframe (e.g. `fs-mask` to keep rendered PII out of session replay). */
  className?: string;
};

/**
 * Sanitizes the layout-authored HTML before it reaches the iframe — the port
 * of Ember's preview whitelist (`cio/helpers/preview-whitelist`). DOMPurify
 * strips scripts, event handlers, and `javascript:`/`data:` URLs while
 * keeping the document structure email HTML needs (html/head/body, style,
 * link, tables).
 */
function sanitizeLayoutHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    WHOLE_DOCUMENT: true,
    // Email layouts carry their styles in <style>/<link> tags (Ember's
    // whitelist allowed both); DOMPurify still applies its URL checks to the
    // link href.
    ADD_TAGS: ['link'],
    ADD_ATTR: ['target'],
  });
}

/**
 * Sanitizes the layout body, then injects the trusted Foundation stylesheet.
 * The `{% include_foundation_css %}` tag is dropped before sanitization (so it
 * never renders as literal text), and the `<style>` block is spliced into the
 * sanitized `<head>` afterward — keeping DOMPurify's CSS pass from stripping
 * Foundation's `@media` rules.
 */
function buildPreviewDoc(body: string): string {
  const sanitized = sanitizeLayoutHtml(body.replace(FOUNDATION_LIQUID_TAG, ''));
  return sanitized.includes('<head>')
    ? sanitized.replace('<head>', `<head>${FOUNDATION_CSS_STYLE_TAG}`)
    : `${FOUNDATION_CSS_STYLE_TAG}${sanitized}`;
}

const SAFE_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

function handleLinkClick(event: Event) {
  const el = event.target as HTMLElement;
  const anchor = el.tagName === 'A' ? el : el.closest('a');

  if (anchor != null) {
    event.preventDefault();
    const href = anchor.getAttribute('href');
    if (href == null) {
      return;
    }
    // Defense in depth on top of sanitization: this handler runs in the
    // parent window, outside the iframe sandbox, so never hand `window.open`
    // a scheme like `javascript:` that the browser would evaluate.
    let url: URL;
    try {
      url = new URL(href, window.location.href);
    } catch {
      return;
    }
    if (SAFE_LINK_PROTOCOLS.has(url.protocol)) {
      // Sever the opener so the opened page can't reach back into the app
      // (reverse tab-nabbing).
      window.open(url.href, '_blank', 'noopener,noreferrer');
    }
  }
}

/**
 * Renders the layout body in an auto-growing iframe — port of Ember's
 * `HtmlPreview`.
 *
 * The body is sanitized (like Ember's whitelist pass) *and* loaded via
 * `srcDoc` in a sandbox without `allow-scripts`, so scripts can't run at the
 * platform level either. `allow-same-origin` (safe here since no scripts can
 * execute inside) lets the parent measure the content for auto-height and
 * intercept link clicks to open them in a new tab, matching Ember's
 * behavior.
 */
export function HtmlPreview({ body, className }: HtmlPreviewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const srcDoc = buildPreviewDoc(body);

  const updateHeight = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    if (iframe == null || doc == null) {
      return;
    }
    // `documentElement.getBoundingClientRect().height` is fractional and
    // includes the <body> margin; Math.ceil closes Chrome's sub-1px phantom
    // scrollbar gap. Don't use `documentElement.scrollHeight` — it's floored
    // at the iframe's clientHeight so the frame never shrinks back. (Same
    // reasoning as the Ember component; see FLO-3453.)
    const preciseHeight =
      doc.documentElement == null ? 0 : Math.ceil(doc.documentElement.getBoundingClientRect().height);
    const height = Math.max(preciseHeight, doc.body?.scrollHeight ?? 0);
    iframe.style.height = `${Math.max(MIN_HEIGHT, height)}px`;
  }, []);

  const handleLoad = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (doc == null) {
      return;
    }

    doc.removeEventListener('click', handleLinkClick);
    doc.addEventListener('click', handleLinkClick);

    // The injected email HTML pulls in the foundation stylesheet, fonts, and
    // images that finish loading after the initial `load` event. Observing
    // the body keeps the iframe sized to the final laid-out content.
    resizeObserverRef.current?.disconnect();
    if (doc.body != null) {
      resizeObserverRef.current = new ResizeObserver(updateHeight);
      resizeObserverRef.current.observe(doc.body);
    }
    updateHeight();
  }, [updateHeight]);

  useEffect(() => {
    return () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
    };
  }, []);

  return (
    <iframe
      ref={iframeRef}
      title="HTML preview"
      className={className}
      sandbox="allow-same-origin"
      srcDoc={srcDoc}
      onLoad={handleLoad}
      style={{ width: '100%', border: 'none', height: `${MIN_HEIGHT}px` }}
    />
  );
}
